import { memo } from 'react';

import type { DrillHole } from '~api/services';
import type { ColourScale } from '~utils';
import type { Coords } from './DrillHolesLayerBase';
import { buildCoords, DrillHolesLayerBase } from './DrillHolesLayerBase';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type NeighbouringDrillHolesLayerProps = {
  /** Neighbouring drill holes to render */
  drillHoles: DrillHole[];
  /** Optional colour scale */
  colourScale?: ColourScale;
  /** Currently selected hole identifier (hole_name) */
  selectedHoleId: string | null;
  /** Called when a hole is activated/selected */
  onHoleSelect: (hole: DrillHole) => void;
  /** Optional formatter for the title attribute of each hole */
  titleFormatter?: (hole: DrillHole) => string;
  /**
   * Which position to use for rendering the hole marker.
   * Defaults to 'toe' — the bottom of the hole, which better represents the drilled destination.
   */
  collarOrToe?: 'collar' | 'toe';
};

/* -------------------------------------------------------------------------- */
/*                         Module-level selector functions                    */
/* -------------------------------------------------------------------------- */

/** Reads the collar (surface entry point) from GeoJSON coordinates */
const getCollarCoords = (hole: DrillHole): Coords | null =>
  buildCoords(hole.collar_position.coordinates[0], hole.collar_position.coordinates[1]);

/** Reads the toe (bottom of the hole) from GeoJSON coordinates */
const getToeCoords = (hole: DrillHole): Coords | null =>
  buildCoords(hole.toe_position.coordinates[0], hole.toe_position.coordinates[1]);

/** Resolve property values by name so the base can apply discrete/continuous scales. */
const getPropertyValue = (hole: DrillHole, property: string): unknown => {
  if (property === 'drill_model' && hole.drill_model != null) {
    return String(hole.drill_model).toUpperCase();
  }

  return hole[property as keyof DrillHole] ?? null;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Adapter layer that renders neighbouring `DrillHole` records as SVG circles.
 * Wraps `DrillHolesLayerBase` with typed selectors for the DrillHole schema.
 *
 * Colour scaling is supported via `pattern_name` (discrete) and numeric DrillHole properties.
 * Defaults to rendering at the toe position.
 */
const NeighbouringDrillHolesLayerInner = ({
  drillHoles,
  colourScale,
  selectedHoleId,
  onHoleSelect,
  titleFormatter,
  collarOrToe = 'toe',
}: NeighbouringDrillHolesLayerProps) => {
  const getCoords = collarOrToe === 'collar' ? getCollarCoords : getToeCoords;

  return (
    <DrillHolesLayerBase
      drillHoles={drillHoles}
      getHoleId={(hole) => `${hole.pattern_name}/${hole.hole_name}`}
      getPlannedCoords={getCoords}
      getActualCoords={getCoords}
      getPropertyValue={getPropertyValue}
      colourScale={colourScale}
      selectedHoleId={selectedHoleId}
      onHoleSelect={onHoleSelect}
      titleFormatter={titleFormatter}
      type="neighbouring"
    />
  );
};

const NeighbouringDrillHolesLayer = memo(NeighbouringDrillHolesLayerInner);
NeighbouringDrillHolesLayer.displayName = 'NeighbouringDrillHolesLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { NeighbouringDrillHolesLayer, type NeighbouringDrillHolesLayerProps };
